/**
 * Take-Home calculation adapter
 * Simple interface to existing tax calculation utilities for the Simple Take-Home Calculator
 */

import { calculateAll } from '../calculations/taxCalculations';

/**
 * Get annual take-home pay from gross annual salary
 * Uses existing tax calculation logic with minimal defaults
 * @param {number} annualGross - Annual gross salary
 * @param {object} config - Tax configuration object from ConfigContext
 * @returns {number} Annual take-home pay
 */
export function getAnnualTakeHomeFromGross(annualGross, config) {
  if (!annualGross || annualGross <= 0) {
    return 0;
  }

  // Simple user input with just salary - no overtime, pension, or student loan
  const userInput = {
    salary: annualGross,
    overtime: { include: false, value: 0, inputType: 'amount' },
    pension: {
      active: false,
      contribution: 0,
      inputType: 'percentage',
      scheme: 'Auto-enrolment',
      calculationBasis: 'Qualifying Earnings',
      includeOvertime: false,
    },
    studentLoanPlan: { plan: "none" }
  };

  // Use existing calculation logic
  const result = calculateAll(userInput, config);
  
  return result.takeHome || 0;
}

/**
 * Format currency in GBP using existing site conventions
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export function formatCurrencyGBP(amount) {
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(String(amount).replace(/,/g, '')));
}

/**
 * Get input mode preference from localStorage
 * @returns {string} 'annual' or 'monthly', defaults to 'annual'
 */
export function getInputModeFromStorage() {
  try {
    const stored = localStorage.getItem('sth_input_mode');
    return stored === 'monthly' ? 'monthly' : 'annual';
  } catch (error) {
    // Fallback for private browsing or localStorage errors
    return 'annual';
  }
}

/**
 * Set input mode preference to localStorage
 * @param {string} mode - 'annual' or 'monthly'
 */
export function setInputModeToStorage(mode) {
  try {
    if (mode === 'annual' || mode === 'monthly') {
      localStorage.setItem('sth_input_mode', mode);
    }
  } catch (error) {
    // Silently fail for private browsing
  }
}

/**
 * Convert monthly salary to annual
 * @param {number} monthly - Monthly salary
 * @returns {number} Annual salary
 */
export function monthlyToAnnual(monthly) {
  return monthly * 12;
}

/**
 * Convert annual salary to monthly
 * @param {number} annual - Annual salary
 * @returns {number} Monthly salary
 */
export function annualToMonthly(annual) {
  return annual / 12;
}